import Cookies from 'js-cookie';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Empty, Form, message, Select, Space, Spin, Tabs } from 'antd';
import Chart from 'react-google-charts';
import dayjs from 'dayjs';
import * as XLSX from "xlsx";
import { FileExcelOutlined, PushpinFilled } from "@ant-design/icons";
import constants from '../../../../constants/constants';
import APIUtils from '../../../../api/APIUtils';
import WebLinks from '../../../../api/WebLinks';
import { convertFirstCharToUpperCase } from '../../../../globalFunctions/GlobalFunctions';
import { CommonContext } from '../../../../Context';

const Suppliers = () => {
  const getDefaultDates = () => {
    const toDate = dayjs().format(constants.DateFormat2);
    const fromDate = dayjs().subtract(1, "month").format(constants.DateFormat2);
    return { fromDate, toDate };
  };
  const [loader, setLoader] = useState(true);
  const [apiData, setApiData] = useState([]);
  const [activeKey, setActiveKey] = useState(constants.paidAppsBySupplierByOccupation);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { fromDate: defaultFrom, toDate: defaultTo } = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [hiddenSuppliers, setHiddenSuppliers] = useState([]);
  const [parentKey, setParentKey] = useState("occupation");
  const [supplierColorMap, setSupplierColorMap] = useState({});
  const [apiCache, setApiCache] = useState({});
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const commonContext=useContext(CommonContext);
  const buyerId = commonContext.common.buyerId;

  useEffect(() => {
    if (TAB_CONFIG[activeKey]?.hasDateFilter) {
      if (fromDate && toDate) {
        getDashboardUrl();
      }
    } else {
      getDashboardUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, buyerId, fromDate, toDate]);

  const getSuppliers = (apiData, hiddenSuppliers = []) => {
    const allSuppliers = [...new Set(apiData.map(item => item.name))];
    const visibleSuppliers = allSuppliers.filter(
      s => !hiddenSuppliers.includes(s)
    );
    return { allSuppliers, visibleSuppliers };
  };
  const TAB_CONFIG = {
    [constants.paidAppsBySupplierByOccupation]: {
      label: constants.PaidAppsBySupplierByOccupation,
      apiRoute: WebLinks.GetMultiSupplierByOccupationPaid,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "apps",
      groupValue: "occupation",
    },
    [constants.cpaBySupplierByOccupation]: {
      label: constants.CPABySupplierByOccupation,
      apiRoute: WebLinks.GetMultiSupplierByOccupationPaid,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "cpa",
      groupValue: "occupation",
    },
    [constants.interviewsBySupplierByOccupation]: {
      label: constants.InterviewsBySupplierByOccupation,
      apiRoute: WebLinks.GetMultiSupplierByOccupation,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "interview",
      groupValue: "occupation",
      additionalText: constants.DateOfApplication
    },
    [constants.cpiBySupplierByOccupation]: {
      label: constants.CPIBySupplierByOccupation,
      apiRoute: WebLinks.GetMultiSupplierByOccupationPaid,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "cpi",
      groupValue: "occupation",
      additionalText: constants.DateOfApplication
    },
    [constants.hiresBySupplierByOccupation]: {
      label: constants.HiresBySupplierByOccupation,
      apiRoute: WebLinks.GetMultiSupplierByOccupation,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "hired",
      groupValue: "occupation",
      additionalText: constants.DateOfApplication
    },
    [constants.cphBySupplierByOccupation]: {
      label: constants.CPHBySupplierByOccupation,
      apiRoute: WebLinks.GetMultiSupplierByOccupationPaid,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "cph",
      groupValue: "occupation",
      additionalText: constants.DateOfApplication
    },
    [constants.paidAppsBySupplierByJobTitle]: {
      label: constants.PaidAppsBySupplierByJobTitle,
      apiRoute: WebLinks.GetMultiSupplierByJobTitle,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "apps",
      groupValue: "jobTitle",
    },
    [constants.cpaBySupplierByJobTitle]: {
      label: constants.CPABySupplierByJobTitle,
      apiRoute: WebLinks.GetMultiSupplierByJobTitle,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "cpa",
      groupValue: "jobTitle",
    },
    [constants.interviewsBySupplierByJobTitle]: {
      label: constants.InterviewsBySupplierByJobTitle,
      apiRoute: WebLinks.GetMultiSupplierByJobTitle,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "interview",
      groupValue: "jobTitle",
      additionalText: constants.DateOfApplication
    },
    [constants.cpiBySupplierByJobTitle]: {
      label: constants.CPIBySupplierByJobTitle,
      apiRoute: WebLinks.GetMultiSupplierByJobTitle,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "cpi",
      groupValue: "jobTitle",
      additionalText: constants.DateOfApplication
    },
    [constants.hiresBySupplierByJobTitle]: {
      label: constants.HiresBySupplierByJobTitle,
      apiRoute: WebLinks.GetMultiSupplierByJobTitle,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "hired",
      groupValue: "jobTitle",
      additionalText: constants.DateOfApplication
    },
    [constants.cphBySupplierByJobTitle]: {
      label: constants.CPHBySupplierByJobTitle,
      apiRoute: WebLinks.GetMultiSupplierByJobTitle,
      chartType: "ColumnChart",
      options: () => chartOptions,
      hasDateFilter: true,
      valueKey: "cph",
      groupValue: "jobTitle",
      additionalText: constants.DateOfApplication
    },
  };
  const TAB_GROUPS = {
    occupation: [
      constants.paidAppsBySupplierByOccupation,
      constants.interviewsBySupplierByOccupation,
      constants.hiresBySupplierByOccupation,
      constants.cpiBySupplierByOccupation,
      constants.cphBySupplierByOccupation,
      constants.cpaBySupplierByOccupation,
    ],
    jobTitle: [
      constants.paidAppsBySupplierByJobTitle,
      constants.interviewsBySupplierByJobTitle,
      constants.hiresBySupplierByJobTitle,
      constants.cpiBySupplierByJobTitle,
      constants.cphBySupplierByJobTitle,
      constants.cpaBySupplierByJobTitle,
    ],
  };

  const chartOptions = useMemo(() => {
    const { allSuppliers } = getSuppliers(apiData);
    const visibleSuppliers = allSuppliers.filter(
      s => !hiddenSuppliers.includes(s)
    );
    return {
      titlePosition: "none",
      chartArea: { top: 20, width: "80%", height: "80%" },
      legend: "none",
      hAxis: { format: "0" },
      vAxis: { minValue: 0, format: "0" },
      colors: visibleSuppliers.map(s => supplierColorMap[s]),
    };
  }, [supplierColorMap, hiddenSuppliers,apiData]);

  const getCacheKey = (key) => {
    let base = TAB_CONFIG[key]?.apiRoute;
    if (TAB_CONFIG[key]?.hasDateFilter) {
      return `${base}_${buyerId}_${fromDate}_${toDate}`;
    }
    return `${base}_${buyerId}`;
  };

  const buildChartData = (apiData, valueKey) => {
    if (!apiData || apiData.length === 0) return [];

    const { allSuppliers } = getSuppliers(apiData);
    const suppliers = allSuppliers.filter(
      s => !hiddenSuppliers.includes(s)
    );
    const occupations = [...new Set(apiData.map(item => item[TAB_CONFIG[activeKey].groupValue]))];
    const titleText = [`${convertFirstCharToUpperCase(TAB_CONFIG[activeKey].groupValue)}`, ...suppliers];
    const dataMap = {};
    apiData.forEach(item => {
      const occ = item[TAB_CONFIG[activeKey].groupValue];
      const supplier = item.name;
      if (!dataMap[occ]) dataMap[occ] = {};
      let value = item[valueKey];
      if (valueKey !== "apps") {
        value = parseFloat(value.toFixed(2));
      }
      dataMap[occ][supplier] = value ?? 0;
    });
    const formattedData = [
      titleText,
      ...occupations.map(occ => [
        occ,
        ...suppliers.map(s => dataMap[occ]?.[s] ?? 0)
      ])
    ];
    return formattedData;
  };
  const formattedText = (apiData) => {
    const valueKey = TAB_CONFIG[activeKey]?.valueKey;
    if (!valueKey) return [];

    return buildChartData(apiData, valueKey);
  };
  const chartData = useMemo(() => {
    if (!apiData || apiData.length === 0) return [];
    return formattedText(apiData);
  }, [apiData, hiddenSuppliers, activeKey]);

  const buildDashboardUrl = (activeKey) => {
    let params = `buyerId=${buyerId}`;
    if (TAB_CONFIG[activeKey]?.hasDateFilter) {
      params += `&startDate=${fromDate}&endDate=${toDate}`;
    }
    return `${TAB_CONFIG[activeKey]?.apiRoute}?${params}`;
  };

  const getDashboardUrl = async () => {
    const cacheKey = getCacheKey(activeKey);
    if (apiCache[cacheKey]) {
      const cachedData = apiCache[cacheKey];
      setHiddenSuppliers([]);
      setApiData(cachedData);
      setLoader(false);
      setIsTabSwitching(false);
      return;
    }
    try {
      setLoader(true);
      const url = buildDashboardUrl(activeKey);
      const response = await APIUtils.apiCall("GET", url);
      if (!response.isCatch && response.resultObj.statusCode === "2000") {
        const apiData = response.resultObj.data || [];
        setApiCache((prev) => ({
          ...prev,
          [cacheKey]: apiData,
        }));
        const uniqueSuppliers = [...new Set(apiData.map((item) => item.name))];
        setSupplierColorMap((prev) => {
          const updated = { ...prev };
          uniqueSuppliers.forEach((s) => {
            if (!updated[s]) {
              updated[s] =
                constants.GOOGLE_COLORS[
                Object.keys(updated).length % constants.GOOGLE_COLORS.length
              ];
            }
          });
          return updated;
        });
        setApiData(apiData);
        setIsTabSwitching(false);
      } else {
        setApiData([]);
        message.error("Failed to fetch data");
      }
    } catch (error) {
      console.error("API Error:", error);
      setApiData([]);
      message.error("Something went wrong while fetching data");
    } finally {
      setLoader(false);
    }
  };

  const handleDateChange = (type, date, dateString) => {
    if (type === "from") {
      if (toDate && dayjs(dateString).isAfter(dayjs(toDate))) {
        message.error("From date cannot be after To date");
        return;
      }
      setFromDate(dateString);
    }
    if (type === "to") {
      if (fromDate && dayjs(dateString).isBefore(dayjs(fromDate))) {
        message.error("To date cannot be before From date");
        return;
      }
      setToDate(dateString);
    }
    setHiddenSuppliers([]);
  };

  const disableDate = (type, current) => {
    if (!current || current > dayjs().endOf("day")) return true;

    const compareDate = type === "from" ? toDate : fromDate;
    if (!compareDate) return false;

    return type === "from"
      ? current > dayjs(compareDate)
      : current < dayjs(compareDate);
  };

  const handleChangeActiveKey = (key) => {
    setIsTabSwitching(true);
    setActiveKey(key);
    const cacheKey = getCacheKey(key);
    if (!apiCache[cacheKey]) {
      setLoader(true);
    } else {
      setTimeout(() => setIsTabSwitching(false), 50);
    }
  };
  const hasData = chartData ? chartData?.length > 1 : false;
  const downloadFile = (tabName) => {
    try {
      if (!apiData || apiData.length === 0) {
        message.error("No data to download");
        return;
      }
      setDownloadLoading(true);
      const { valueKey, groupValue } = TAB_CONFIG[activeKey];
      const getGroupLabel = () =>
        groupValue === "jobTitle" ? "Job Title" : "Occupation";
      const getValueLabel = () => valueKey.toUpperCase();
      const excelData = apiData.map(item => {
        let value = item[valueKey];
        if (valueKey !== "apps") {
          value = parseFloat(value.toFixed(2));
        }
        return {
          [getGroupLabel()]: item[groupValue],
          Supplier: item.name,
          [getValueLabel()]: value ?? 0,
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      const fileName = TAB_CONFIG[activeKey]?.hasDateFilter
        ? `${tabName} ${fromDate} - ${toDate}.xlsx`
        : `${tabName}.xlsx`;
      setTimeout(() => {
        XLSX.writeFile(workbook, fileName);
        setDownloadLoading(false);
      }, 300);
    } catch (err) {
      setDownloadLoading(false);
      message.error("Error downloading file");
    } finally {
      setLoader(false);
    }
  };

  const renderHeader = (tabName) => (
    <div className="flex justify-between items-center flex-wrap gap-2">
      <div className="flex items-center gap-1 text-sm text-gray-600">
        <PushpinFilled className="text-red-500" />
        <span>
          Click on{" "}
          {TAB_CONFIG[activeKey].groupValue === "jobTitle"
            ? "Job Title"
            : "Supplier"}{" "}
          to show or hide it in the chart.
        </span>
      </div>
      <Button
        className="bg-green-800 text-white hover:!text-white hover:!border-green-500"
        icon={<FileExcelOutlined />}
        onClick={() => downloadFile(tabName)}
        loading={downloadLoading}
      >
        Download as Excel
      </Button>
    </div>
  );
  const displayDate = () => {
    return (
      <Form>
        <Space>
          <Form.Item label="From" className="mb-2">
            <DatePicker
              onChange={(date, dateString) => handleDateChange("from", date, dateString)}
              value={fromDate ? dayjs(fromDate) : null}
              disabledDate={(current) => disableDate("from", current)}
              allowClear={false}
            />
          </Form.Item>
          <Form.Item label="To" className="mb-2">
            <DatePicker
              onChange={(date, dateString) => handleDateChange("to", date, dateString)}
              value={toDate ? dayjs(toDate) : null}
              disabledDate={(current) => disableDate("to", current)}
              allowClear={false}
            />
          </Form.Item>
        </Space>
      </Form>
    );
  };

  const handleClickSupplierChange = (supplier) => {
    const isHidden = hiddenSuppliers?.includes(supplier);
    const { visibleSuppliers } = getSuppliers(apiData, hiddenSuppliers);
    if (!isHidden && visibleSuppliers.length === 1) {
      return;
    }
    setHiddenSuppliers((prev) =>
      isHidden
        ? prev.filter((s) => s !== supplier)
        : [...prev, supplier]
    );
  }

  const groupItems = Object.entries(TAB_GROUPS).map(([groupKey, tabKeys]) => {
    const childItems = tabKeys
      .map((key) => {
        const config = TAB_CONFIG[key];
        if (!config) return null;

        return {
          key: String(key),
          label: config.label,
          children: (
            <>
              {renderHeader(config.label)}
              <div className="text-center my-3 text-xl">
                {config.label}{config.additionalText}{config?.hasDateFilter ? ` - ${fromDate} to ${toDate}` : ""}
              </div>
              <div className="relative w-full min-h-[450px]">
                {(loader || isTabSwitching) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-20">
                    <Spin />
                  </div>
                )}
                <div className="flex flex-col lg:flex-row gap-4 w-full">
                  <div className="custom-legend flex flex-wrap lg:flex-col gap-2 order-2 lg:order-1 
                    max-h-[200px] overflow-y-auto lg:max-h-[500px] lg:overflow-y-auto lg:flex-nowrap min-w-[200px]">
                    {getSuppliers(apiData).allSuppliers.map(
                      (supplier) => {
                        const isHidden = hiddenSuppliers.includes(supplier);
                        return (
                          <div
                            key={supplier}
                            onClick={() => { handleClickSupplierChange(supplier)}}
                            className={`custom-legend-item w-fit lg:w-full ${isHidden ? "opacity-40 line-through" : ""}`}
                          >
                            <span
                              className="custom-legend-color"
                              style={{ "--legend-color": supplierColorMap[supplier] }}
                            />
                            <span className="text-xs">{supplier}</span>
                          </div>
                        );
                      }
                    )}
                  </div>
                  <div className="flex-1 order-1 lg:order-2">
                    <div className="relative w-full h-[600px] flex items-center justify-center">
                      <div className="relative w-full h-[600px]">
                        {!loader && !isTabSwitching && hasData && (
                          <Chart
                            key={`${activeKey}-${hiddenSuppliers.join(",")}`}
                            chartType={config.chartType}
                            width="100%"
                            height="600px"
                            data={chartData}
                            options={chartOptions}
                          />
                        )}
                        {!loader && !isTabSwitching && !hasData && (
                          <Empty className="mt-5" description={constants.NO_DATA} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ),
        };
      })
      .filter(Boolean);
    return {
      key: groupKey,
      label: groupKey === "occupation" ? "Occupation" : "Job Title",
      children: (
        <>
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 w-full">
            <div className="flex flex-wrap items-end gap-3">
              {displayDate()}
            </div>
          </div>
          <Tabs
            activeKey={activeKey}
            onChange={handleChangeActiveKey}
            items={childItems}
          />
        </>
      ),
    };
  });
  const handleParentChange = (key) => {
    setParentKey(key);
    const cacheKey = getCacheKey(activeKey);
    if (!apiCache[cacheKey]) {
      setHiddenSuppliers([]);
      setApiData([]);
    }
    const firstTab = TAB_GROUPS[key][0];
    setActiveKey(firstTab);
  };
  return (
    <div>
      <Tabs activeKey={parentKey} onChange={handleParentChange} items={groupItems} />
    </div>
  );
};
export default Suppliers;
